import { Directive, Input } from "@angular/core";

import { coerceNumberProperty, coerceBooleanProperty, BooleanInput, NumberInput } from "@vattenfall/util";

import { InputsElementAccessor } from "./inputs-accessor";

/**
 * The `TextsElementAccessor` contains methods and properties shared by all free-text-like input components, like password, search, tel, text and url.
 */
@Directive()
export class TextsElementAccessor extends InputsElementAccessor<string, HTMLInputElement> {
  /**
   * Whether the form field should stretch the entire width of its parent. Default is `false`.
   */
  @Input()
  get fluid(): boolean {
    return this._fluid;
  }
  set fluid(value: BooleanInput) {
    this._fluid = coerceBooleanProperty(value);
  }
  private _fluid = false;
  /**
   * The initial value of the control.
   *
   * A string value or an array of string values in case the input is a multi-select option list.
   */
  @Input()
  get value(): string | null {
    return this._value;
  }
  set value(value: string | null) {
    this._value = value;
    this.setProperty("value", this._value);
    if (this.ngControl) this.ngControl.control?.setValue(value);
  }
  private _value: string | null = null;
  /**
   * The pattern attribute, when specified, is a regular expression that the input's value must match in order for the value to pass constraint validation.
   */
  @Input()
  get pattern(): string | null {
    return this._pattern;
  }
  set pattern(value: string | null) {
    this._pattern = value;
    this.setProperty("pattern", this._pattern);
  }
  private _pattern: string | null = null;
  /**
   * An attribute which, if present, indicates that the user should not be able to edit the value of the input.
   */
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: BooleanInput) {
    this._readonly = coerceBooleanProperty(value);
    this.setProperty("readOnly", this._readonly);
  }
  private _readonly = false;
  /**
   * Maximum length (number of characters) of `value`.
   *
   * Default is `null`.
   */
  @Input()
  get maxlength(): number | null {
    return this._maxlength;
  }
  set maxlength(value: NumberInput) {
    this._maxlength = coerceNumberProperty(value);
    this.setProperty("maxLength", this._maxlength);
  }
  private _maxlength: number | null = null;
  /**
   * Minimum length (number of characters) of `value`.
   *
   * Default is `null`.
   */
  @Input()
  get minlength(): number | null {
    return this._minlength;
  }
  set minlength(value: NumberInput) {
    this._minlength = coerceNumberProperty(value);
    this.setProperty("minLength", this._minlength);
  }
  private _minlength: number | null = null;
}
